import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Per-list message editor: Text, Email subject, Email body.
 * Merge-field "+ Insert" helpers drop tokens at the cursor so users never
 * type bracket syntax. Prospecting lists hide the amount / last-gift
 * inserts because those donors have no giving history.
 *
 * Loads from and saves to the same endpoint on both sides:
 *   - Owner portal: /admin/callers/{callerId}/categories/{catId}/messages
 *   - Candidate  : /caller/categories/{catId}/messages
 *
 * Same reset endpoint pattern with a POST /messages/reset suffix.
 */
export function MessageEditor({ scope, callerId, categoryId, categoryType, categoryName, testIdPrefix }) {
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [initial, setInitial] = useState({ text: "", subject: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const textRef = useRef(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const readUrl = scope === "owner"
    ? `/admin/callers/${callerId}/categories/${categoryId}/messages`
    : `/caller/categories/${categoryId}/messages`;
  const writeUrl = scope === "owner"
    ? `/admin/callers/${callerId}/categories/${categoryId}/messages`
    : `/caller/categories/${categoryId}/messages`;
  const resetUrl = scope === "owner"
    ? `/admin/callers/${callerId}/categories/${categoryId}/messages/reset`
    : `/caller/categories/${categoryId}/messages/reset`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(readUrl);
      setText(data.text_message || "");
      setSubject(data.email_subject || "");
      setBody(data.email_body || "");
      setInitial({
        text: data.text_message || "",
        subject: data.email_subject || "",
        body: data.email_body || "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [readUrl]);

  useEffect(() => { load(); }, [load]);

  const dirty = text !== initial.text || subject !== initial.subject || body !== initial.body;

  const insertToken = (field, token) => {
    const refMap = { text: textRef, subject: subjectRef, body: bodyRef };
    const setterMap = {
      text: setText,
      subject: setSubject,
      body: setBody,
    };
    const valueMap = { text, subject, body };
    const el = refMap[field].current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = valueMap[field].slice(0, start);
    const after = valueMap[field].slice(end);
    const next = before + token + after;
    setterMap[field](next);
    // Restore cursor after the inserted token on the next tick.
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + token.length;
      try { el.setSelectionRange(cursor, cursor); } catch { /* noop */ }
    });
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.patch(writeUrl, {
        text_message: text,
        email_subject: subject,
        email_body: body,
      });
      toast.success(`"${categoryName}" messages saved`);
      setInitial({ text, subject, body });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (resetting) return;
    if (!window.confirm(`Reset "${categoryName}" messages to the ${categoryType === "prospecting_list" ? "Prospecting List" : "Call List"} default? Your current edits will be lost.`)) return;
    setResetting(true);
    try {
      const { data } = await api.post(resetUrl);
      setText(data.text_message || "");
      setSubject(data.email_subject || "");
      setBody(data.email_body || "");
      setInitial({
        text: data.text_message || "",
        subject: data.email_subject || "",
        body: data.email_body || "",
      });
      toast.success(`"${categoryName}" reset to default`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  const isProspecting = categoryType === "prospecting_list";
  // Tokens: dollar / last-gift ones only surface on Call Lists.
  const tokens = [
    { label: "First name", token: "[FIRST_NAME]" },
    !isProspecting && { label: "Ask amount", token: "[REMAINING_MAX]" },
    { label: "City", token: "[CITY]" },
    { label: "State", token: "[STATE]" },
    !isProspecting && { label: "Last gift", token: "[LAST_GIFT]" },
    !isProspecting && { label: "Last gift date", token: "[LAST_GIFT_DATE]" },
  ].filter(Boolean);

  if (loading) {
    return <div className="text-xs text-slate-400 italic p-4">Loading messages…</div>;
  }

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-editor`}>
      {/* Text message */}
      <TokenField
        label="Text message"
        subtitle="Sent as SMS. Also copied when the caller taps Copy."
        value={text}
        onChange={setText}
        inputRef={textRef}
        rows={7}
        tokens={tokens}
        onInsert={(t) => insertToken("text", t)}
        testIdPrefix={`${testIdPrefix}-text`}
      />

      {/* Email subject (single-line, small token set) */}
      <TokenField
        label="Email subject"
        subtitle="Subject line for the email button."
        value={subject}
        onChange={setSubject}
        inputRef={subjectRef}
        rows={1}
        tokens={tokens}
        onInsert={(t) => insertToken("subject", t)}
        testIdPrefix={`${testIdPrefix}-subject`}
      />

      {/* Email body */}
      <TokenField
        label="Email message"
        subtitle="Body of the email. Signature is added automatically."
        value={body}
        onChange={setBody}
        inputRef={bodyRef}
        rows={9}
        tokens={tokens}
        onInsert={(t) => insertToken("body", t)}
        testIdPrefix={`${testIdPrefix}-body`}
      />

      <p className="text-[11px] text-slate-500 leading-snug">
        Insert codes with the buttons above. Each code fills in per donor when
        the message is opened. Any code with no value (e.g. a donor missing a
        city) is shown as blank — the code itself is never sent.
      </p>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-flag-navy text-white rounded hover:bg-flag-navy/90 disabled:opacity-40"
          data-testid={`${testIdPrefix}-save`}
        >
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={resetting}
          className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-40"
          data-testid={`${testIdPrefix}-reset`}
        >
          {resetting ? "Resetting…" : "Reset to default"}
        </button>
      </div>
    </div>
  );
}

function TokenField({ label, subtitle, value, onChange, inputRef, rows, tokens, onInsert, testIdPrefix }) {
  const isSingleLine = rows <= 1;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
          {subtitle && <div className="text-[10.5px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 my-1">
        {tokens.map((t) => (
          <button
            key={t.token}
            type="button"
            onClick={() => onInsert(t.token)}
            className="px-2 py-0.5 text-[10.5px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-800 rounded"
            data-testid={`${testIdPrefix}-insert-${t.token.replace(/[[\]{}]/g, "").toLowerCase()}`}
            title={`Insert ${t.token}`}
          >
            + {t.label}
          </button>
        ))}
      </div>
      {isSingleLine ? (
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-flag-navy/30 focus:border-flag-navy"
          data-testid={`${testIdPrefix}-input`}
        />
      ) : (
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-flag-navy/30 focus:border-flag-navy font-mono leading-snug"
          data-testid={`${testIdPrefix}-input`}
        />
      )}
    </div>
  );
}
